"use client";

/**
 * ComputerSoundPill — "Playing from your computer", with Stop.
 *
 * Shown while the computer's sound plays and no browser pane is on screen: the
 * person switched the computer's tab, closed it, or went to another page, and
 * the music kept going. Clicking it brings the computer back on Chrome; Stop
 * turns the sound off, and the stream then rests the way it always has.
 *
 * When the sound stops by itself (an hour without activity, or the box's
 * three-hour cap), its place says so in one quiet line for a few seconds, then
 * empties.
 *
 * One capsule of the transport family (ConnectivityCapsule, UpdatingCapsule):
 * the same top-center slot and surface. It yields while one of those is up, for
 * the reason they give each other: two capsules at once say two things at once.
 * Mounted once in AppShell.
 */
import { useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { SpeakerIcon } from "@/components/icons/SpeakerIcon";
import { openBrowserForTask } from "@/lib/open-browser-for-task";
import {
  isPlayingInBackground,
  useComputerSoundStore,
} from "@/stores/computerSoundStore";
import { useConnectionHealthStore } from "@/stores/connectionHealthStore";
import { useConnectionStore } from "@/stores/connectionStore";
import { useTaskStore } from "@/stores/taskStore";

/** How long the "sound stopped" line stays. */
export const SOUND_NOTICE_MS = 6_000;

/** Why the sound stopped by itself, in the person's words. */
const NOTICE_TEXT = {
  idle: "Sound stopped after an hour without activity",
  cap: "Sound stopped after 3 hours",
} as const;

export function ComputerSoundPill() {
  const playing = useComputerSoundStore(isPlayingInBackground);
  const taskId = useComputerSoundStore((s) => s.taskId);
  const notice = useComputerSoundStore((s) => s.notice);
  const healthy = useConnectionHealthStore((s) => s.health === "healthy");
  const inDeployWindow = useConnectionStore(
    (s) => !!s.deployUpdating || !!s.deployUpdatedTick
  );
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    if (!notice) return;
    const id = window.setTimeout(
      () => useComputerSoundStore.getState().clearNotice(),
      SOUND_NOTICE_MS
    );
    return () => window.clearTimeout(id);
  }, [notice]);

  if (!healthy || inDeployWindow) return null;

  if (notice) {
    return (
      <div
        className="fixed top-3 left-1/2 -translate-x-1/2 w-max max-w-[calc(100vw-1rem)] z-[84] pointer-events-none"
        data-testid="computer-sound-notice"
        role="status"
        aria-live="polite"
      >
        <div className="inline-flex items-center gap-2 rounded-full border border-border-default bg-bg-elevated px-4 py-1.5 text-3.5 text-fg-muted shadow-connectivity-capsule">
          <SpeakerIcon muted aria-hidden />
          <span>{NOTICE_TEXT[notice]}</span>
        </div>
      </div>
    );
  }

  if (!playing) return null;

  const openComputer = () => {
    if (!taskId) return;
    openBrowserForTask(taskId);
    const onTask =
      pathname.startsWith("/task") &&
      useTaskStore.getState().selectedTaskId === taskId;
    if (!onTask) router.push(`/task/${taskId}`);
  };

  return (
    <div
      className="fixed top-3 left-1/2 -translate-x-1/2 w-max max-w-[calc(100vw-1rem)] z-[84]"
      data-testid="computer-sound-pill"
      role="status"
    >
      <div className="inline-flex items-center gap-1 rounded-full border border-border-default bg-bg-elevated p-1 text-3.5 font-medium text-fg-default shadow-connectivity-capsule">
        <button
          type="button"
          onClick={openComputer}
          disabled={!taskId}
          className="inline-flex items-center gap-2 rounded-full px-3 py-1 transition-colors hover:bg-bg-muted disabled:hover:bg-transparent"
        >
          <SpeakerIcon aria-hidden />
          <span>Playing from your computer</span>
        </button>
        <button
          type="button"
          onClick={() => useComputerSoundStore.getState().turnOff()}
          className="rounded-full px-3 py-1 text-fg-muted transition-colors hover:bg-bg-muted hover:text-fg-default"
        >
          Stop
        </button>
      </div>
    </div>
  );
}
